import { getMessages } from 'locales';
import { ProductMetadata } from 'models/batch/import-metadata';
import { ProductVariationBase } from 'models/productsReferential';
import { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import { ordersBasketImportFailed, ordersBasketInitFormRequested } from 'state/orders-basket/actions';
import { getUserPreferencesLanguage } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { getOrdersBasketWorkspaceTabIdByProductSubType } from 'state/workspaces/ordersBasket/tabContent/tabContent.selectors';
import { parseExcel } from 'utils/import/excelExtractor';
import sumitomoFieldMetadataJson from 'utils/import/fieldMetadata/Sumitomo.json';
import { ImportFormRequest } from 'utils/import/import.models';
import { buildImportRequests } from 'utils/import/utils/parseFileUtils';
import {
  createProductReverseMap,
  createProductRows,
  getUniqueProductVariations,
} from 'utils/import/utils/productMappingUtils';
import { getToastUtils } from 'utils/toast';

const importFailedTitle = 'toast.import.failed';

export function importSpreadsheetToOrdersBasketThunk(
  requests: ImportFormRequest[],
  productType: string,
  productSubType: string,
): Thunk<void> {
  return (dispatch, getState, { getNewGuid }) => {
    const toast = getToastUtils(dispatch);

    if (!requests?.length) {
      toast.warn({ id: 'quote.import.noValidRows' }, { id: importFailedTitle });
      return;
    }

    // Filter requests to only include those with matching productType and productSubType
    const validRequests = requests.filter(
      request => request.productType === productType && request.productSubType === productSubType,
    );

    if (!validRequests.length) {
      toast.warn({ id: 'quote.import.noValidRows' }, { id: importFailedTitle });
      return;
    }

    const productVariation: ProductVariationBase = { productType, productSubType };
    const tabId = getOrdersBasketWorkspaceTabIdByProductSubType(getState(), productVariation);
    if (!tabId) {
      toast.warn({ id: 'quote.import.noValidTab' }, { id: importFailedTitle });
      return;
    }

    toast.success(
      { id: 'quote.import.ordersBasket.requested', values: { nbOfQuotes: validRequests.length } },
      { id: 'toast.import.inProgress' },
    );

    validRequests.forEach(request => {
      const temporaryNegotiationId = getNewGuid();
      dispatch(ordersBasketInitFormRequested(tabId, temporaryNegotiationId, request));
    });
  };
}

export function ordersBasketInitFormFailedThunk(tabId: string, temporaryFormId: string): Thunk {
  return dispatch => {
    const toast = getToastUtils(dispatch);

    toast.warn(
      {
        id: 'quote.init.failed',
      },
      { id: importFailedTitle },
    );

    dispatch(ordersBasketImportFailed(tabId, temporaryFormId));
  };
}

export function importExcelToOrdersBasketWorkspaceThunk(buffer: ArrayBuffer): Thunk<void> {
  return (dispatch, getState) => {
    const toast = getToastUtils(dispatch);

    const state = getState();
    const locale = getUserPreferencesLanguage(state);
    const { dataMaps, errors } = parseExcel(buffer);
    if (errors.length > 0 || dataMaps.length === 0) {
      toast.error({ id: 'quote.import.ordersBasket.failed' }, { id: 'quote.import.button' });
      return;
    }
    const translationMap = getMessages(locale);
    const productReverseMap = createProductReverseMap(translationMap);
    const rows = createProductRows(dataMaps, translationMap, productReverseMap);
    const uniqueVariations = getUniqueProductVariations(rows);

    if (rows.some(row => !row.productType || !row.productSubType || !row.wrapper) || uniqueVariations.length !== 1) {
      toast.error({ id: 'quote.import.ordersBasket.failed' }, { id: 'quote.import.button' });
      return;
    }
    const { productType, productSubType } = uniqueVariations[0];
    const fieldMetadata: FieldMetadataById = (sumitomoFieldMetadataJson as any).fieldsMetadata;
    const productsMetadata: ProductMetadata[] = [
      {
        fieldsMetadata: fieldMetadata,
        productType,
        productSubType,
      },
    ];

    const validatedRequests = buildImportRequests({ locale, rows, errors: [] }, productsMetadata);
    const allErrors = validatedRequests.flatMap(r => r.validationErrors || []);

    if (allErrors.length > 0) {
      allErrors.forEach(err => {
        toast.error(
          {
            id: 'importFileValidator.fieldValueInvalid',
            values: { row: err.row, field: err.fieldId, errorMsg: err.errorMsg },
          },
          { id: 'quote.import.button' },
          false,
        );
      });
      return;
    } else {
      dispatch(importSpreadsheetToOrdersBasketThunk(validatedRequests, productType, productSubType));
    }
  };
}
